import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
} from "@mui/material";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";
import { getProductsByCategory } from "@smfa/utils/src/common/products";
import StripePaymentModal, {
  PaymentIntentLoader,
  processStripePaymentIntent,
} from "../components/Common/StripePayment";
import { createPaymentIntent } from "../actions/payment.actions";
import { objectToQueryString } from "../utils/helper";

/**
 * Membership Categories page component
 * @returns {JSX.Element}
 * @component
 */
const MembershipCategories = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [membershipProducts, setMembershipProducts] = useState([]);

  // Get auth and user state
  const { isAuthenticated } = useSelector((state) => state.auth) || {};
  const { data: userData } = useSelector((state) => state.user) || {};
  const membershipPayment = userData?.membership?.membership; // Access the payment details which include expiration
  const membershipTerm = membershipPayment?.term;

  // Keep banner/intro data from Redux for now
  const { loading, membership: { data: membershipData } = {} } =
    useSelector((state) => state.pageContents) || {};

  // New state for Stripe payment
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(false); // Loading for Payment Intent creation
  const [currentPlanForPayment, setCurrentPlanForPayment] = useState(null); // To store { price, planType, name }

  // Calendar-year renewal data from server
  const membershipRenewal = userData?.membership?.renewal || {};
  const renewalCheckout = membershipRenewal.checkout || {};
  const renewalTargetTerm = membershipRenewal.target_term || null;
  const pricingPeriodLabel =
    userData?.membership?.catalog?.pricing_period_label ||
    "per membership term";
  const canRenew =
    renewalCheckout.is_available ||
    membershipRenewal.should_warn ||
    !membershipPayment ||
    membershipPayment._is_expired;

  // Determine membership status
  const { hasActiveMembership, activePlanType } = useMemo(() => {
    if (!isAuthenticated || !membershipPayment) {
      return { hasActiveMembership: false, activePlanType: null };
    }
    const isActive = !membershipPayment._is_expired;
    return {
      hasActiveMembership: isActive,
      activePlanType: isActive ? membershipPayment.plan_type : null,
    };
  }, [isAuthenticated, membershipPayment]);

  // Fetch and filter membership products
  useEffect(() => {
    setError(null);
    try {
      const products = getProductsByCategory("membership");
      const filteredProducts = products.filter((p) => !p.admin_only);
      setMembershipProducts(filteredProducts);
    } catch (err) {
      log.error("Error fetching membership products:", err);
      setError("Could not load membership categories. Please try again later.");
    }
  }, []);

  // Placeholder for Stripe payment initiation
  const handlePayNow = ({ event, amount, plan_type, name }) => {
    event.preventDefault();
    // This function is called only for authenticated users due to button logic.
    setCurrentPlanForPayment({ amount, planType: plan_type, name });
    setPaymentAmount(amount);
    loadClientSecretInternal({ amount, plan_type });
  };

  const loadClientSecretInternal = ({ amount, plan_type }) => {
    if (!userData || !userData.id) {
      setError(
        "User data is not available. Please log in again or refresh the page.",
      );
      return;
    }
    setSubmitProgress(true);
    setError(null); // Clear previous errors

    const paymentDetails = {
      amount,
      user_id: userData.id,
      plan_type,
      linked_table: "online_application",
      linked_id: userData.profile.id,
      email: userData.email, // Pass email for Stripe intent
    };
    if (renewalCheckout.membership_term_key) {
      paymentDetails.membership_term_key = renewalCheckout.membership_term_key;
    } else if (membershipRenewal.target_year) {
      paymentDetails.membership_year = membershipRenewal.target_year;
    }

    processStripePaymentIntent({
      createPaymentIntentAction: () => createPaymentIntent(paymentDetails),
      paymentDetails,
      setLoading: setSubmitProgress,
      onSuccess: ({
        clientSecret: newClientSecret,
        free_plan,
        paymentId,
        application_id,
        applicationId,
      }) => {
        if (free_plan) {
          setSubmitProgress(false);
          navigate(
            `/payment-complete${objectToQueryString({
              payment_type: "membership_payment",
              user_id: userData?.id,
              plan_type: plan_type,
              application_id:
                application_id || applicationId || userData?.profile?.id,
              payment_id: paymentId,
              free_plan: true,
            })}`,
          );
        } else {
          setClientSecret(newClientSecret);
          setPaymentDialog(true);
        }
      },
      onError: (errorMessage) => {
        setError(
          errorMessage || "Failed to initialize payment. Please try again.",
        );
      },
    });
  };

  const handlePaymentDialogClose = (event, reason) => {
    if (isPaymentProcessing && reason === "backdropClick") {
      // Prevent closing if payment is processing via backdrop click
      // The StripePaymentModal itself has a confirmation for closing during processing.
      return;
    }
    setPaymentDialog(false);
    setClientSecret(""); // Clear client secret
    setCurrentPlanForPayment(null); // Reset selected plan
  };

  const handlePaymentProcess = (processing) => {
    setIsPaymentProcessing(processing);
  };

  return (
    <PageLayout
      pageName="Membership Categories"
      showLoader={loading}
      checkComingSoon
    >
      {/* Banner - Kept from original, uses membershipData */}
      {membershipData?.membershipbanner ? (
        <PageBanner
          bannerImage={membershipData.membershipcategoriesbanner.banner_image}
          mainTitle={
            membershipData.membershipcategoriesbanner.banner_title ||
            "Membership Categories"
          }
          pageTitle="Membership"
        />
      ) : (
        <PageBanner mainTitle="Membership Categories" pageTitle="Membership" />
      )}

      {/* Content Section */}
      <Box component="section" sx={{ pb: 8 }}>
        <Container>
          {/* Membership Categories Grid - Updated to use fetched products */}
          <div className="section-title bene-title cate-title">
            <p className="member-para-sec">
              {membershipData?.membershipcategories?.section_description}
            </p>
          </div>

          {/* Admin status banner */}
          {isAuthenticated && userData?.role === "admin" && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Administrator Account</AlertTitle>
              You are signed in with administrator privileges.
            </Alert>
          )}

          {/* Renewal/expired info banner */}
          {isAuthenticated &&
            membershipRenewal.should_warn &&
            !membershipPayment?._is_expired && (
            <Alert
              severity={membershipPayment ? "info" : "warning"}
              sx={{ mb: 3 }}
            >
              {membershipRenewal.display?.renewal_banner ||
                membershipRenewal.display?.expired_banner ||
                `Your membership is active. Renew for ${renewalTargetTerm?.term_label || "the next membership term"}.`}
            </Alert>
          )}
          {isAuthenticated &&
            (membershipRenewal.has_next_term ||
              membershipRenewal.has_next_year) && (
              <Alert severity="success" sx={{ mb: 3 }}>
                You are already paid for{" "}
                {userData?.membership?.next?.term?.term_label ||
                  renewalTargetTerm?.term_label ||
                  "the next membership term"}
                .
              </Alert>
            )}
          {isAuthenticated && membershipPayment?._is_expired && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {membershipRenewal.display?.expired_banner ||
                `Your ${membershipTerm?.term_label || "previous membership"} has expired. Select a plan below to purchase ${renewalTargetTerm?.term_label || "the current membership term"}.`}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 4 }}>
              {error}
            </Alert>
          ) : membershipProducts.length > 0 ? (
            <Grid container spacing={4}>
              {membershipProducts.map((product, index) => {
                const isCurrentUserPlan =
                  hasActiveMembership && product.plan_type === activePlanType;
                // Allow purchasing if: not logged in, no active membership, it's the current plan, or in the renewal window
                const isRenewalEligible = hasActiveMembership && canRenew;
                const cardOpacity =
                  hasActiveMembership &&
                  !isCurrentUserPlan &&
                  !isRenewalEligible
                    ? 0.6
                    : 1;
                const showButton =
                  !isAuthenticated ||
                  !hasActiveMembership ||
                  isCurrentUserPlan ||
                  isRenewalEligible;
                const isFree = product.price === 0;

                return (
                  <Grid
                    size={{ xs: 12, md: 6, lg: 4 }}
                    key={`product-${product.plan_type || index}`}
                  >
                    <Card
                      elevation={2}
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderTop: `5px solid ${product.borderColor || "transparent"}`,
                        opacity: cardOpacity, // conditional opacity
                        transition:
                          "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, opacity 0.2s ease-in-out",
                        "&:hover": {
                          transform:
                            hasActiveMembership &&
                            !isCurrentUserPlan &&
                            !isRenewalEligible
                              ? "none"
                              : "translateY(-5px)", // Disable hover effect for dimmed cards
                          boxShadow:
                            hasActiveMembership &&
                            !isCurrentUserPlan &&
                            !isRenewalEligible
                              ? 2
                              : 6,
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, position: "relative" }}>
                        {isCurrentUserPlan && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Current Plan"
                            color="success"
                            size="small"
                            sx={{ position: "absolute", top: 16, right: 16 }}
                          />
                        )}
                        <Typography
                          variant="h5"
                          gutterBottom
                          sx={{
                            // Use product.color if needed, e.g., color: `${product.color}.main`
                            fontWeight: "medium",
                          }}
                        >
                          {product.name} {/* Use product name */}
                        </Typography>

                        <Typography
                          variant="body1"
                          gutterBottom
                          sx={{
                            mt: 2,
                            color: "text.secondary", // Use secondary color for description
                          }}
                        >
                          {/* Use long description or fallback to short */}
                          {product.description_long || product.description}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          {product.price > 0
                            ? `$${product.price.toFixed(2)} ${pricingPeriodLabel}`
                            : "Complimentary"}
                        </Typography>
                      </CardContent>

                      {showButton && (
                        <CardActions
                          sx={{ p: 2, pt: 0, justifyContent: "center" }}
                        >
                          {isCurrentUserPlan && !isRenewalEligible ? (
                            <Button
                              variant="contained"
                              disabled
                              color="success"
                            >
                              Your Current Plan
                            </Button>
                          ) : (
                            <Button
                              component={!isAuthenticated ? Link : "button"} // Link if logged out, button if logged in
                              to={
                                !isAuthenticated
                                  ? `/signup?plan=${product.plan_type}`
                                  : undefined
                              }
                              onClick={
                                isAuthenticated
                                  ? (e) =>
                                      handlePayNow({
                                        event: e,
                                        amount: product.price,
                                        plan_type: product.plan_type,
                                        name: product.name, // product name
                                      })
                                  : undefined
                              }
                              variant="contained"
                              sx={{
                                backgroundColor: `${product.color}.main`,
                                "&:hover": {
                                  backgroundColor: `${product.color}.dark`,
                                },
                              }}
                            >
                              {isRenewalEligible
                                ? renewalCheckout.cta_label ||
                                  `Renew for ${renewalTargetTerm?.term_label || "Next Term"}`
                                : isFree
                                  ? product.buttonText || "Register Now"
                                  : product.buttonText || "Join Us"}
                            </Button>
                          )}
                        </CardActions>
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="text.secondary">
                No membership categories available at this time.
              </Typography>
              <Button
                component={Link}
                to="/contact"
                variant="outlined"
                color="primary"
                sx={{ mt: 2 }}
              >
                Contact Us for More Information
              </Button>
            </Paper>
          )}

          {/* Bottom CTA - Conditionally render based on authentication */}
          {!isAuthenticated && (
            <Paper
              elevation={1}
              sx={{
                p: 4,
                mt: 8,
                borderRadius: 2,
                bgcolor: "background.paper",
                textAlign: "center",
              }}
            >
              <Typography variant="h5" gutterBottom>
                Ready to join the SMFA community?
              </Typography>
              <Typography variant="body1">
                If you have questions about which membership category is right
                for you, please contact us.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  component={Link}
                  to="/signup"
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{ mx: 1 }}
                >
                  Join Now
                </Button>
                <Button
                  component={Link}
                  to="/contact"
                  variant="outlined"
                  color="primary"
                  size="large"
                  sx={{ mx: 1 }}
                >
                  Contact Us
                </Button>
              </Box>
            </Paper>
          )}
        </Container>
      </Box>

      {/* Stripe Payment Modal */}
      <StripePaymentModal
        open={paymentDialog}
        onClose={handlePaymentDialogClose}
        clientSecret={clientSecret}
        amount={paymentAmount}
        onPaymentProcess={handlePaymentProcess}
        successParams={{
          payment_type: "membership_payment",
          user_id: userData?.id,
          plan_type: currentPlanForPayment?.planType,
          application_id: userData?.profile?.id,
        }}
        dialogTitle={
          currentPlanForPayment
            ? `Complete Payment for ${currentPlanForPayment.name}`
            : "Complete Your Membership Payment"
        }
      />

      {/* Loading overlay for payment intent creation */}
      <PaymentIntentLoader show={submitProgress} />
    </PageLayout>
  );
};

export default MembershipCategories;
